export type MS = number;

export type ServiceLocation =
  | 'inStore'
  | 'home'
  | 'storeRemote'
  | 'remote'
  | 'storeHome';

export const getLocationType = (
  locationType: ServiceLocation,
): ServiceLocation => {
  if (['remote', 'storeRemote'].includes(locationType)) {
    return 'remote';
  }
  if (['home', 'storeHome'].includes(locationType)) {
    return 'home';
  }
  return 'inStore';
};

export function asyncWait(timeToWait: MS): Promise<void> {
  return new Promise((resolve): void => {
    setTimeout((): void => {
      resolve();
    }, timeToWait);
  });
}

/* global CSS_CONFIG */
const { MULTIPLETIMEZONES } = CSS_CONFIG;
const { enableConsolidatedCalendar } = CSS_CONFIG.SERVICES;

export const isConsolidatedCalendarEnabled = (
  currentService: string,
): boolean => {
  if (enableConsolidatedCalendar.default === false) {
    return false;
  }
  if (
    enableConsolidatedCalendar.default === true &&
    enableConsolidatedCalendar.excludedServices.includes(currentService)
  ) {
    return false;
  }
  return true;
};

export function removeTimePartFromDate(date: string): string {
  const [datePart] = date.split('T');
  return datePart;
}

export const isServiceLocationStore = (location: ServiceLocation): boolean => {
  return location === 'inStore';
};

export const isServiceProvidedRemote = (location: ServiceLocation): boolean => {
  return location === 'remote' || location === 'storeRemote';
};

export const isServiceProvidedAtHome = (location: ServiceLocation): boolean => {
  return location === 'home' || location === 'storeHome';
};

export const isTimeInBrowserSettings = (location: ServiceLocation): boolean => {
  return (
    MULTIPLETIMEZONES &&
    (location === 'home' || location === 'remote' || location === 'storeHome')
  );
};

export const isStoreRemoteInConsolidatedCalendar = (
  location: ServiceLocation,
  currentService: string,
): boolean => {
  return (
    isConsolidatedCalendarEnabled(currentService) && location === 'storeRemote'
  );
};

export const isDistanceServiceProvidedByStore = (
  location: ServiceLocation,
): boolean => {
  return location === 'storeRemote' || location === 'storeHome';
};

export const getServiceLocationKey = (location: ServiceLocation): string => {
  if (isServiceProvidedAtHome(location)) {
    return 'home';
  }
  return location;
};

export const isStoreService = (location: ServiceLocation): boolean => {
  switch (location) {
    case 'inStore':
    case 'storeRemote':
    case 'storeHome':
      return true;
    case 'home':
    default:
      return false;
  }
};

export const isOutOfStoreService = (location: ServiceLocation): boolean => {
  return location === 'home' || location === 'remote';
};

// This is to allow remote services in one-time-zone only to show
// the timezone of the remote store that's hosting the remote appointment
// than the timezone from the user's browser. It prevents the offset to
// be added and timezone to localize.
// https://jira.digital.ingka.com/browse/SELFS-2764
export const showTimeInStoreTZ = (location: ServiceLocation): boolean => {
  const remoteServiceInMultiTZCountry =
    isOutOfStoreService(location) && MULTIPLETIMEZONES;
  return !remoteServiceInMultiTZCountry;
};

export const startOfWeekDayToNumber = (day: string): 0 | 1 => {
  if (day.toUpperCase() === 'SUNDAY') {
    return 0;
  }
  return 1;
};
